<!--
  WARNING!

  <roi-portlet> is deprecated use <portlet-ce> instead.
  Do not add any new features to this component.
  This component will be removed in the future.
-->
<svelte:options
    customElement={{
        tag: 'roi-portlet',
        /**
         * Since svelte4, custom properties and methods on HTMLCustomElement are not yet available
         * until the component mounts. We add a `tick` method to custom element class in order to
         * make it more semantically obvious why we have to wait before invoking methods.
         *
         * https://github.com/sveltejs/svelte/issues/8954
         * https://github.com/sveltejs/svelte/pull/8457
         */
        // @ts-ignore sadly this block cannot be typed, so we have to suppress the error
        extend: customElementConstructor => {
            return class extends customElementConstructor {
                tick() {
                    return Promise.resolve();
                }
            };
        },
    }}
/>

<script lang="ts">
    import { tick, onDestroy, onMount } from 'svelte';
    import { writable } from 'svelte/store';
    import { Observable, Subject } from 'rxjs';
    import { isString, pick } from 'lodash-es';
    import { Logger, noop, themeCSSParse } from '@bayer-int/portletce-shared';
    import type { Cache, LogLevel } from '@bayer-int/portletce-shared';

    import type { ExternalAppAction } from './store';
    import { buildStore, AppAction } from './store/index';
    import { developmentBoot, noneAuthBoot, prodBoot } from 'portlet/config/boot';
    import { safeImportMetaUrl } from 'portlet/config/importMetaUrl';
    import { isProd } from 'portlet/config/env';
    import { buildRootService } from 'portlet/config/service';
    import { Attributes, isBoolean, isNumber } from 'portlet/common/utils';
    import { externalCss } from 'portlet/common/utils/directives';
    import {
        setStoreContext,
        setLoggerContext,
        setServiceContext,
        setStoreInterfaceContext,
    } from 'portlet/common/contexts';

    import {
        Specification,
        type Setting,
        type ViewId,
        FilterDataArray,
        SpecStatus,
        type RawConfig,
    } from 'portlet/models';
    import { Config, SettingKeys } from 'portlet/models';
    import Modal from 'portlet/common/components/portletMenu/PopOver.svelte';
    import Portlet from './Portlet.svelte';
    import type { Signal } from '@bayer-int/portletce-shared';
    import type { VoidCallback } from './common/components/portletMenu/types';
    import { createInstanceApi, applyFilterMapping } from './instanceApi';
    import {
        allowedExternalActionsFilter,
        listenerActionsFilter,
        publishableActionsFilter,
        attachSenderToPublishableAction,
        listenerActionToDomEvent,
    } from './store/actions';

    import { STYLE_TAG_PREFIX } from './common/components/index';
    import { fromActions } from '@rxsv/core';
    import type { ReinitializePersistingOptions } from './store/init';
    import { type TelemetryConfig, telemetryDefaultConfig } from './services/TelemetryService';
    // As a webcomponent, setting attributes in HTML produces strings,
    // setting properties gives anything. We err on side of caution
    // and assume everything is `unknown`.
    //
    // Unused generic parameter just for better semantics
    type AttributeValue<ExpectedType> = unknown;
    interface Props {
        pid?: AttributeValue<string>;
        noauth?: AttributeValue<string>;
        /**
         * Configures which set of endpoints the portlet will use.
         * This is different from `isProd` from `env.ts`, because `isProd`
         * only signifies wether the build was in production mode.
         * `env` attribute controls used endpoints regardless of build mode.
         */
        env?: AttributeValue<string>;
        src?: AttributeValue<string>;
        namespace?: AttributeValue<string>;
        nocache?: AttributeValue<boolean>;
        viewid?: AttributeValue<number>;
        stylesheet?: AttributeValue<string | { css: string }>;
        nocdn?: AttributeValue<boolean>;
        local?: AttributeValue<boolean>;
        width?: AttributeValue<string>;
        height?: AttributeValue<string>;
        externalevents?: AttributeValue<string>;
        uniqueid?: AttributeValue<string>;
        noheader?: AttributeValue<boolean>;
        nomenu?: AttributeValue<boolean>;
        nomandatorysettinglabel?: AttributeValue<boolean>;
        externalmenudialog?: AttributeValue<boolean>;
        versionstatus?: AttributeValue<SpecStatus>;
        suppressautodate?: AttributeValue<boolean>;
        settings?: AttributeValue<Setting[]>;
        spec?: AttributeValue<Specification>;
        suspended?: AttributeValue<boolean>;
        maximized?: AttributeValue<boolean>;
        log?: AttributeValue<Logger | LogLevel>;
        signals?: AttributeValue<Signal[]>;
        filters?: AttributeValue<FilterDataArray>;
        fetchcache?: AttributeValue<Cache>;
        disablechartanimations?: AttributeValue<boolean>;
        telemetry?: AttributeValue<TelemetryConfig>;
        disableerrormonitoring?: AttributeValue<boolean>;
        performancethreshold?: AttributeValue<number>;
        locale?: AttributeValue<string>;
    }

    let {
        pid = undefined,
        noauth = undefined,
        env = undefined,
        src = undefined,
        namespace = undefined,
        nocache = undefined,
        viewid = undefined,
        stylesheet = undefined,
        nocdn = undefined,
        local = undefined,
        width = undefined,
        height = undefined,
        externalevents = undefined,
        uniqueid = undefined,
        noheader = undefined,
        nomenu = undefined,
        nomandatorysettinglabel = undefined,
        externalmenudialog = undefined,
        versionstatus = undefined,
        suppressautodate = undefined,
        settings = undefined,
        spec = undefined,
        suspended = undefined,
        maximized = undefined,
        log = undefined,
        signals = undefined,
        filters = undefined,
        fetchcache = undefined,
        disablechartanimations = undefined,
        telemetry = telemetryDefaultConfig,
        disableerrormonitoring = undefined,
        performancethreshold = undefined,
        locale = navigator.language,
    }: Props = $props();

    let uniqueIdAttr = isString(uniqueid) ? uniqueid : undefined;
    let defaultLogger = Logger.fromUnknown(log);
    let loggerContext = writable(defaultLogger);
    let bootfile = window.portletce?.bootfile || prodBoot;
    let serviceContext = writable(buildRootService(bootfile, Config.defaults, defaultLogger));
    let component = $state(null as HTMLElement | null);
    let mounted = $state(false);
    let cleanup = $state(noop as VoidCallback);
    const [store, storeInterface, storeInit, storeDeInit] = buildStore(uniqueIdAttr);

    let isFirstLoad = true;

    let currentState = $derived(store.state$);
    let currentService = $derived(serviceContext);

    const instanceApi = createInstanceApi(() => $currentState, defaultLogger, uniqueIdAttr);

    // makes methods available on the HTMLElement
    export const uniqueId = instanceApi.uniqueId;
    export const dispatchFilter = instanceApi.dispatchFilter;
    export const dispatchFilterOfType = instanceApi.dispatchFilterOfType;
    export const setSignal = instanceApi.setSignal;
    export const setSignals = instanceApi.setSignals;
    export const setSetting = instanceApi.setSetting;
    export const setSettings = instanceApi.setSettings;
    export const setFilters = instanceApi.setFilters;
    export const reloadData = instanceApi.reloadData;
    export const getState = instanceApi.getters;

    setStoreContext(store);
    setStoreInterfaceContext(storeInterface);
    setLoggerContext(loggerContext);
    setServiceContext(serviceContext);

    function initPortlet() {
        /**
         * It turns out that in web-component mode `onMount` may be called multiple times,
         * e.g. when it is reattached to the DOM. Since the configuration that happens in `onMount`
         * should happen only once, we make an early return.
         */
        const logger = Logger.fromUnknown(log);

        logger.warn('<roi-portlet> is deprecated use <portlet-ce> instead.');

        if (mounted) return;

        mounted = true;

        const { config, additionalAttrs, spec } = parseConfigAndAttrs();
        const isDev = !isProd || config.env === 'dev';
        const bootfile =
            window.portletce?.bootfile ||
            (config.noauth ? noneAuthBoot : isDev ? developmentBoot : prodBoot);
        const service = buildRootService(bootfile, config, logger);

        if (isProd && !config.disableerrormonitoring) service.errorMonitoringService.initialize();
        storeInit(service);
        store.action$.next(AppAction.portlet.PORTLET_INIT({ config, additionalAttrs, spec }));
        // we fetch user data only once on mount, it may be needed to fetch on each PORTLET_INIT in the future
        store.action$.next(AppAction.auth.FETCH_USER_INFO_REQ());

        loggerContext.set(logger);
        serviceContext.set(service);

        const externalEventCleanup = isString(externalevents)
            ? connectToExternalSubject(config, externalevents)
            : noop;

        const subscription = instanceApi.events$.subscribe(store.action$);
        const storeActionsSubscription = store.action$
            .pipe(listenerActionsFilter, listenerActionToDomEvent)
            .subscribe(event => {
                forwardEvent(event);
            });

        const reinitializationSubscription = store.action$
            .pipe(fromActions(AppAction.portlet.EXTERNAL_REINITIALIZATION))
            .subscribe(({ payload }) => reinitialize(payload));

        const apiSubscriptionCleanup = () => subscription.unsubscribe();
        const storeSubscriptionCleanup = () => storeActionsSubscription.unsubscribe();
        const reinitializationSubscriptionCleanup = () =>
            reinitializationSubscription.unsubscribe();

        cleanup = () => {
            externalEventCleanup();
            apiSubscriptionCleanup();
            storeSubscriptionCleanup();
            reinitializationSubscriptionCleanup();
            // clean up reducers to free memoized results to prevent leaks for large datasets
            store.action$.next(AppAction.portlet.PORTLET_RESET());
            store.action$.next(
                AppAction.filters.RESET_SETTINGS_AND_FILTERS({
                    preserveFilters: false,
                    preserveSignals: false,
                }),
            );
            store.action$.next(AppAction.email.RESET_EMAIL_DATA());
            store.action$.next(AppAction.view.RESET_VIEW());
            store.action$.complete();
            store.effect$.complete();
            service.cache.clear();
            storeDeInit();
        };
    }

    initPortlet();

    onDestroy(() => {
        if (!mounted) return;

        mounted = false;
        cleanup();
        cleanup = noop;
    });

    $effect(() => {
        if (pid) reinitialize();
    });

    $effect(() => {
        if (noauth) reinitialize();
    });

    $effect(() => {
        if (src) reinitialize();
    });

    $effect(() => {
        if (spec) reinitialize();
    });

    $effect(() => {
        if (isBoolean(suspended)) toggleSuspended(suspended);
    });

    $effect(() => {
        if (isNumber(viewid)) changeView(viewid);
    });

    $effect(() => {
        if (settings) setSettings(settings);
    });

    $effect(() => {
        if (signals) setSignals(signals);
    });

    $effect(() => {
        if (filters) setFilters(filters);
    });

    async function reinitialize({
        persistFilters = false,
        persistSignals = ['THEME'], // We want preserve THEME signal by default
    }: Partial<ReinitializePersistingOptions> = {}) {
        if (!mounted || isFirstLoad) {
            isFirstLoad = false;
            return;
        }
        /** Wait single tick, otherwise component uses old viewid` */
        await tick();

        const { config, additionalAttrs, spec } = parseConfigAndAttrs();

        const resetActions = [
            AppAction.portlet.PORTLET_RESET(),
            AppAction.filters.RESET_SETTINGS_AND_FILTERS({
                preserveFilters: persistFilters,
                preserveSignals: persistSignals,
            }),
            AppAction.portlet.PORTLET_INIT({ config, additionalAttrs, spec }),
        ];

        storeInit($currentService);
        resetActions.forEach(action => store.action$.next(action));
    }

    function toggleSuspended(isSuspended: boolean) {
        store.action$.next(isSuspended ? AppAction.view.SUSPEND() : AppAction.view.RESUME());
    }

    function changeView(viewId: ViewId) {
        store.action$.next(
            AppAction.filters.CHANGE_SETTING_VALUE({
                key: SettingKeys.VIEWID,
                value: viewId,
            }),
        );
    }

    function parseConfigAndAttrs() {
        const rootElement = component?.getRootNode();
        const attributes =
            rootElement instanceof ShadowRoot
                ? Attributes.parseNodeNamedMap(rootElement.host.attributes)
                : {};

        const props = {
            pid,
            noauth,
            env,
            src,
            namespace,
            nocache,
            viewid,
            stylesheet,
            nocdn,
            local,
            uniqueid,
            width,
            height,
            versionstatus,
            suppressautodate,
            fetchcache,
            disablechartanimations,
            locale,
            disableerrormonitoring,
            performancethreshold,
        };

        const config = Config.parse({
            ...readAttributesFromScriptSearchParam(),
            ...readInheritedAttributesFromAncestor(),
            ...Attributes.omitUndefinedKeys({
                pid,
                noauth,
                env,
                src,
                namespace,
                nocache,
                viewid,
                stylesheet,
                nocdn,
                local,
                uniqueid,
                settings,
                versionstatus,
                suppressautodate,
                signals,
                fetchcache,
                disablechartanimations,
                telemetry,
                locale,
                disableerrormonitoring,
                performancethreshold,
            }),
        });

        const additionalAttrs = Attributes.attributeDifference(attributes, props);

        return { config, additionalAttrs, spec };
    }

    function connectToExternalSubject(config: Config, externalevents: string) {
        window.portletce = window.portletce || {};
        window.portletce[externalevents] =
            window.portletce[externalevents] || new Subject<ExternalAppAction>();

        const subscriptionToExternalCleanup = subscribeEventsFrom(window.portletce[externalevents]);
        const subscriptionOfExternalCleanup = emitEventsTo(
            window.portletce[externalevents],
            config.uniqueid,
        );

        return () => {
            subscriptionToExternalCleanup();
            subscriptionOfExternalCleanup();
        };
    }

    export function emitEventsTo(
        subject: Subject<ExternalAppAction>,
        id = uniqueIdAttr,
        mapping: Record<string, string> = {},
    ): VoidCallback {
        if (!id) {
            defaultLogger.warn('You need to provide `uniqueid` in order to publish portlet events');
            return noop;
        }

        const subscription = store.action$
            .pipe(
                publishableActionsFilter,
                attachSenderToPublishableAction(id),
                applyFilterMapping(mapping),
            )
            .subscribe(subject);

        return () => subscription.unsubscribe();
    }

    export function subscribeEventsFrom(subject: Observable<AppAction>): VoidCallback {
        const subscription = subject.pipe(allowedExternalActionsFilter).subscribe(store.action$);

        return () => subscription.unsubscribe();
    }

    export function forceCleanup() {
        cleanup();
        cleanup = noop;
    }

    /**
     * Allows for providing default attribute values via url when loading the portlet script.
     * E.g.
     * <script type="module" src="<PATH>/portlet.js?env=dev">
     * or
     * import '<PATH>/portlet.js?env=dev'
     */
    function readAttributesFromScriptSearchParam(): RawConfig {
        const importUrl = safeImportMetaUrl();

        if (!importUrl) return {};

        return {
            // convert absent (null) parameters to undefined, so that it works just like html tag attributes
            pid: importUrl.searchParams.get('pid') ?? undefined,
            noauth: importUrl.searchParams.get('noauth') ?? undefined,
            env: importUrl.searchParams.get('env') ?? undefined,
            src: importUrl.searchParams.get('src') ?? undefined,
            namespace: importUrl.searchParams.get('namespace') ?? undefined,
            nocache: importUrl.searchParams.get('nocache') ?? undefined,
            viewid: importUrl.searchParams.get('viewid') ?? undefined,
            stylesheet: importUrl.searchParams.get('stylesheet') ?? undefined,
            nocdn: importUrl.searchParams.get('nocdn') ?? undefined,
            local: importUrl.searchParams.get('local') ?? undefined,
            uniqueid: importUrl.searchParams.get('uniqueid') ?? undefined,
            versionstatus: importUrl.searchParams.get('versionstatus') ?? undefined,
            suppressautodate: importUrl.searchParams.get('suppressautodate') ?? undefined,
        };
    }

    /**
     * It is possible to nest portlets within portlets, e.g. when using HTML renderer and
     * rendering <portlet-ce/> tag inside another portlet. It makes sense to copy some of the configuration
     * of the ancestor portlet to the descendant. That's why we use this function to search for nearest
     * shadow root ancestor.
     */
    function readInheritedAttributesFromAncestor(): RawConfig {
        const rootElement = component?.getRootNode();

        if (!(rootElement instanceof ShadowRoot)) return {};

        const ancestorRootElement = rootElement.host.getRootNode();

        const ancestorAttributes =
            ancestorRootElement instanceof ShadowRoot
                ? Attributes.parseNodeNamedMap(ancestorRootElement.host.attributes)
                : {};

        const inheritableAttributes = [
            'env',
            'namespace',
            'noauth',
            'nocache',
            'nocdn',
            'local',
            'versionstatus',
            'log',
        ];

        return pick(ancestorAttributes, inheritableAttributes);
    }

    function forwardEvent(event: CustomEvent) {
        component?.dispatchEvent(
            new CustomEvent(event.type, {
                detail: event.detail,
                bubbles: true,
                cancelable: true,
                composed: true,
            }),
        );
    }

    function clearSpecialStyles() {
        // since styles have changed, we delete all style elements potentially created by renderers.
        // They will be recreated by renderers as they need them.
        const root = component?.getRootNode();
        const rootElement = root instanceof ShadowRoot ? (root as unknown as Element) : undefined;

        if (!rootElement) return;

        rootElement.querySelectorAll(`[id^=${STYLE_TAG_PREFIX}]`).forEach(style => style.remove());
    }

    async function updateThemeSignal(element: HTMLStyleElement | null) {
        //We need to await one tick to fully apply new style tag for get access to it
        await tick();
        // we need to insert empty object as theme signal to correctly clear theme on bayer-ui selectors
        if (!element) {
            setSignal('THEME', {});
            return;
        }
        const themeRule = element.sheet;
        const themeSignalValue =
            themeRule &&
            themeRule.cssRules[0] instanceof CSSStyleRule &&
            themeCSSParse(themeRule.cssRules[0]);
        themeSignalValue && setSignal('THEME', themeSignalValue);
    }

    function onStylesheetUpdate(element: HTMLStyleElement | null) {
        clearSpecialStyles();
        updateThemeSignal(element);
    }
</script>

<svelte:head>
    <style>
        @font-face {
            font-family: 'Material Icons';
            font-style: normal;
            font-weight: 400;
            font-display: block;
            src: url(https://fonts.gstatic.com/s/materialicons/v128/flUhRq6tzZclQEJ-Vdg-IuiaDsNcIhQ8tQ.woff2)
                format('woff2');
        }
    </style>
</svelte:head>
<article
    bind:this={component}
    class="portlet"
    style="height: {height}; width: {width}"
    use:externalCss={{
        styleDef: stylesheet,
        onStyleSheetChange: onStylesheetUpdate,
        service: $currentService.themeService,
        isMounted: mounted,
    }}
>
    {#if mounted}
        <Modal>
            <Portlet
                noheader={Attributes.parseBool(noheader)}
                nomenu={Attributes.parseBool(nomenu)}
                maximized={Attributes.parseBool(maximized)}
                nomandatorysettinglabel={Attributes.parseBool(nomandatorysettinglabel)}
                externalmenudialog={Attributes.parseBool(externalmenudialog)}
                on:portlet-maximize={forwardEvent}
                on:portlet-menu-item-click={forwardEvent}
                on:portlet-menu-item-mouse-enter={forwardEvent}
                on:portlet-menu-item-mouse-leave={forwardEvent}
                on:chartlib-interaction={forwardEvent}
            >
                <slot name="header-prefix" slot="header-prefix" />
                <slot name="header-postfix" slot="header-postfix" />
            </Portlet>
        </Modal>
    {/if}
</article>

<style lang="scss">
    @import '../common/styles/global.scss';
    :host {
        --background-color: white;
        --color: black;

        --border: 0px solid transparent;
        --border-radius: 0;
        --border-color: var(--background-color);

        --font-family: Arial;
        --font-weight: normal;
        --font-size: 1em;

        --title-font-size: 1em;
        --title-font-weight: normal;
        --title-align: center;
        --title-color: var(--color);
        --title-margin: 0;

        --positive-color: green;
        --negative-color: red;

        --header-background-color: var(--background-color);
        --header-color: var(--header-color, var(--color));

        /* portlet and aclib defaults for theming */
        --data-colors: [ '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b',
            '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

        --charts-axes-font-size: 10;
        --table-font-size: 14px;
        --header-min-height: 3.5rem;
    }

    .portlet {
        position: relative;

        height: 100%;
        width: 100%;

        display: flex;
        flex-direction: column;

        border: var(--border);
        border-radius: var(--border-radius);

        font-family: var(--font-family);
        font-weight: var(--font-weight);
        font-size: var(--font-size);
        color: var(--color);
        background-color: var(--background-color);
    }
</style>
